import React, { useState, useRef, useEffect, useMemo } from "react";
import classNames from "classnames";
import SVG from 'components/base/SVG';
import css from "./style.scss";

// Callers may pass either plain objects or raw Immutable.js Maps/Lists (e.g. Redux group/list selectors).
const normalizeItem = (item) => (item && typeof item.toJS === "function" ? item.toJS() : item);

const ListManagementField = ({
  lists = [],
  placeholder = "Select or Type to Create a New List",
  onSelect,
  onCreate,
  label,
  required = false,
  showLabel = true,
  createLabel = "(Create as New List)",
  // Pre-fills the field with a list already known by the caller (e.g. the
  // list a chained modal just saved to, or the list this screen is already
  // scoped to) -- a plain {id, name} item, same shape as `lists` entries.
  // Purely a starting value: typing/selecting still works normally unless
  // `disabled` is also set.
  defaultValue = null,
  // Greys the field out and blocks typing/opening the dropdown, while still
  // showing defaultValue's name -- for a caller that already decided the
  // list and doesn't want it changed here (see InvestorSkipTraceModal).
  disabled = false,
}) => {
  const normalizedLists = useMemo(() => Array.from(lists, normalizeItem), [lists]);
  const normalizedDefault = useMemo(() => normalizeItem(defaultValue), [defaultValue]);

  const [query, setQuery] = useState(normalizedDefault?.name || "");
  const [filtered, setFiltered] = useState(normalizedLists);
  const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const [selectedItem, setSelectedItem] = useState(normalizedDefault);


  useEffect(() => {
    const sorted = [...normalizedLists].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
    );
    setFiltered(sorted);
  }, [normalizedLists]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsActionDropdownOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Tell the caller about the pre-filled value once, on mount -- otherwise a
  // caller reading its own `selectedList` state (to decide whether to enable
  // "Place Order", or whether the list changed) never learns a default was
  // set here at all.
  useEffect(() => {
    if (normalizedDefault) onSelect?.(normalizedDefault);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleInput = (value) => {
    setQuery(value);
    setIsActionDropdownOpen(true);

    if (!value.trim()) {
      setSelectedItem(null);
      setFiltered(normalizedLists);
      onSelect?.(null);
      return;
    }

    if (selectedItem) {
      setSelectedItem(null);
    }

    const filteredLists = normalizedLists.filter((l) =>
      (l.name || "").toLowerCase().includes(value.toLowerCase())
    );
    setFiltered(filteredLists);
  };

  const handleSelect = (item) => {
    setQuery(item.name || "");
    setSelectedItem(item);
    setIsActionDropdownOpen(false);
    onSelect?.(item);
    setFiltered(normalizedLists);
  };

  const handleCreate = () => {
    if (!query.trim()) return;
    const exists = normalizedLists.some(
      (l) => (l.name || "").toLowerCase() === query.toLowerCase()
    );
    if (!exists) {
      onCreate?.(query);
      setIsActionDropdownOpen(false);
      setQuery(query);
    }
  };

  const handleKeyDown = (e) => {
    if (!isActionDropdownOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, filtered.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex === filtered.length) handleCreate();
      else if (activeIndex >= 0) handleSelect(filtered[activeIndex]);
    }
  };

  return (
    <div className={css.wrapper} ref={wrapperRef}>
      {showLabel && (
        <label className={css.campaignLabel}>
          {label} {required && <span className={css.requiredAsterisk}> *</span>}
        </label>
      )}
      <div
        className={classNames(css.inputBox, {
          [css.open]: isActionDropdownOpen,
          [css.disabled]: disabled,
        })}
        onClick={() => {
          if (disabled) return;
          setIsActionDropdownOpen(true);
          setFiltered(normalizedLists);
        }}
      >
        <input
          type="text"
          value={query}
          placeholder={placeholder}
          className={css.input}
          disabled={disabled}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => !disabled && setIsActionDropdownOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {!disabled && (
          <SVG
            icon="iconCaretDown"
            className={css.dropdownCaretIcon}
          />
        )}
      </div>

      {!disabled && isActionDropdownOpen && (
        <ul className={css.dropdown}>
          {filtered.length > 0 ? (
            filtered.map((item, index) => (
              <li
                key={item.id}
                className={classNames(css.item, {
                  [css.active]: index === activeIndex,
                  [css.selected]: selectedItem?.id === item.id,
                })}
                onClick={() => handleSelect(item)}
              >
                {item.name}
              </li>
            ))
          ) : (
            <li className={css.noMatch}>No matches found</li>
          )}
          {query &&
            !normalizedLists.some(
              (l) => (l.name || "").toLowerCase() === query.toLowerCase()
            ) && (
              <>
                <li className={css.divider} />
                <li
                  className={classNames(css.createOption, {
                    [css.active]: activeIndex === filtered.length,
                  })}
                  onClick={handleCreate}
                >
                  {query} <span>{createLabel}</span>
                </li>
              </>
            )}
        </ul>
      )}
    </div>
  );
};

export default ListManagementField;